import { zodResolver } from "@hookform/resolvers/zod"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { AuthSplitLayout } from "@/components/auth-split-layout"
import { SocialButtons } from "@/components/social-buttons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { signUp, waitForSession } from "@/lib/auth-client"
import { signUpSchema, type SignUpValues } from "@/lib/auth-schemas"
import { useAuthProviders } from "@/lib/queries/auth-providers"

const searchSchema = z.object({ redirect: z.string().optional() })

export const Route = createFileRoute("/sign-up")({
  component: SignUpRoute,
  validateSearch: searchSchema,
})

function SignUpRoute() {
  const navigate = useNavigate()
  const search = Route.useSearch()
  const providers = useAuthProviders()
  const [formError, setFormError] = useState<string | null>(null)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignUpValues>({ resolver: zodResolver(signUpSchema) })

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null)
    const { error } = await signUp.email({
      name: values.name,
      email: values.email,
      password: values.password,
    })
    if (error) {
      setFormError(error.message ?? "Could not create your account.")
      return
    }
    await waitForSession()
    // First-run (sole persona test — under three minutes from signup to the embed snippet)
    // unless the signup came from an invitation accept page, which redirects back there so
    // the account also joins the inviting organization instead of starting empty.
    await navigate({ to: search.redirect ?? "/first-run" })
  })

  return (
    <AuthSplitLayout>
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-1.5">
          <h2 className="text-3xl font-semibold tracking-tight text-balance">
            Give your Forms somewhere to go
          </h2>
          <p className="text-sm text-muted-foreground text-pretty">
            Create a workspace, then send your first Submission in a few minutes.
          </p>
        </div>

        <SocialButtons
          intent="sign-up"
          {...(search.redirect === undefined ? {} : { callbackURL: search.redirect })}
        />

        {providers.isLoading ? (
          <div className="flex flex-col gap-3" aria-label="Loading sign-up options">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : providers.data?.email_password.sign_up === true ? (
          <form
            onSubmit={(e) => {
              void onSubmit(e)
            }}
            className="flex flex-col gap-4"
            noValidate
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                autoComplete="name"
                placeholder="Ada Lovelace"
                {...register("name")}
                aria-invalid={errors.name !== undefined}
              />
              {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                {...register("email")}
                aria-invalid={errors.email !== undefined}
              />
              {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                placeholder="At least 8 characters"
                {...register("password")}
                aria-invalid={errors.password !== undefined}
              />
              {errors.password && (
                <p className="text-xs text-destructive">{errors.password.message}</p>
              )}
            </div>

            {formError !== null && (
              <p
                className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                role="alert"
              >
                {formError}
              </p>
            )}

            <Button type="submit" disabled={isSubmitting} className="mt-2">
              {isSubmitting ? "Creating your workspace…" : "Create account"}
            </Button>
          </form>
        ) : (
          <p className="text-sm text-muted-foreground text-pretty">
            Hosted Postbag uses Google or GitHub for new accounts. Existing password accounts can
            still sign in.
          </p>
        )}

        <p className="text-center text-sm text-muted-foreground">
          Already have a workspace?{" "}
          <Link
            to="/sign-in"
            search={search.redirect === undefined ? {} : { redirect: search.redirect }}
            className="font-medium text-primary underline-offset-4 hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
          >
            Sign in
          </Link>
        </p>
      </div>
    </AuthSplitLayout>
  )
}
